import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import KosyamLogo from "@/components/KosyamLogo";
import LanguageToggle from "@/components/LanguageToggle";
import {
  ArrowRight, GraduationCap, Buildings, Lightning, HandHeart, Globe,
  Envelope, MapPin, Sparkle,
} from "@phosphor-icons/react";

const PILLAR_ICONS = [Lightning, HandHeart, Globe];
const PILLAR_KEYS = ["engineering", "human", "global"];

export default function About() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="link-home-header">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden text-primary shrink-0">
              <KosyamLogo size={40} />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-base sm:text-lg font-bold leading-none">Kosyam</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-foreground mt-1 truncate">
                {t("app.brand.tagline_short")}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/"><Button variant="ghost" size="sm" data-testid="btn-back-home">{t("common.home")}</Button></Link>
            <Link to="/login">
              <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full" data-testid="btn-nav-login">
                <span className="hidden sm:inline">{t("auth.login")}</span>
                <span className="sm:hidden">{t("common.get_started")}</span>
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10 opacity-40" aria-hidden
             style={{backgroundImage:'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize:'4px 4px'}} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-14 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary text-[10px] sm:text-xs font-semibold tracking-wide uppercase">
            <Sparkle size={12} weight="fill" /> {t("about.eyebrow")}
          </div>
          <h1 className="mt-6 font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
            {t("about.hero_line1")} <span className="text-accent">{t("about.hero_line1_accent")}</span>{t("about.hero_line1_end")}<br/>
            <span className="text-primary">{t("about.hero_line2")}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl">
            <b className="text-foreground">Nathor Technologies</b> {t("about.hero_intro_a")}
            {" "}<b>{t("about.hero_intro_location")}</b>{t("about.hero_intro_b")}
          </p>
          <div className="mt-7 sm:mt-8 flex flex-wrap gap-3">
            <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer" data-testid="link-nathor-site" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 rounded-full px-8">
                {t("about.cta_visit")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </a>
            <a href="mailto:contact@nathortechnologies.com" data-testid="link-nathor-contact" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8">
                <Envelope size={16} className="mr-2" /> {t("about.cta_contact")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Piliers */}
      <section className="border-b border-border bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-secondary">{t("about.philosophy_eyebrow")}</div>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              {t("about.philosophy_title")}
            </h2>
          </div>
          <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PILLAR_KEYS.map((key, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <div key={key} className="card-hover bg-card rounded-2xl border border-border p-6 sm:p-7" data-testid={`pillar-${key}`}>
                  <div className="w-12 h-12 rounded-lg bg-accent/10 text-accent flex items-center justify-center mb-5">
                    <Icon size={24} weight="duotone" />
                  </div>
                  <div className="font-heading font-semibold text-lg">{t(`about.pillars.${key}_title`)}</div>
                  <div className="text-sm text-muted-foreground mt-2.5 leading-relaxed">{t(`about.pillars.${key}_desc`)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portefeuille produits */}
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-secondary">{t("about.portfolio_eyebrow")}</div>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              {t("about.portfolio_title")}
            </h2>
            <p className="mt-3 text-muted-foreground">{t("about.portfolio_desc")}</p>
          </div>
          <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Link to="/" className="group" data-testid="product-kosyam">
              <div className="h-full card-hover rounded-2xl border p-6 sm:p-7 transition-all bg-primary text-primary-foreground border-primary">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-primary-foreground/10 text-accent">
                    <GraduationCap size={24} weight="duotone" />
                  </div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-primary-foreground/70">
                    {t("about.products.active_badge")}
                  </div>
                </div>
                <div className="font-heading font-bold text-2xl">Kosyam</div>
                <div className="text-sm mt-1 text-primary-foreground/80">{t("app.brand.tagline")}</div>
                <p className="text-sm mt-4 leading-relaxed text-primary-foreground/90">
                  {t("about.products.kosyam_desc")}
                </p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-accent group-hover:gap-2 transition-all">
                  {t("about.products.kosyam_cta")} <ArrowRight size={14} />
                </div>
              </div>
            </Link>
            <div className="rounded-2xl border border-dashed border-border p-6 sm:p-7 flex flex-col justify-center text-center bg-muted/20" data-testid="product-soon">
              <div className="w-12 h-12 mx-auto rounded-lg bg-muted flex items-center justify-center mb-4">
                <Buildings size={24} weight="duotone" className="text-muted-foreground" />
              </div>
              <div className="font-heading font-semibold text-lg text-muted-foreground">{t("about.products.soon_title")}</div>
              <div className="text-sm text-muted-foreground/80 mt-2">
                {t("about.products.soon_desc")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="border-b border-border bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20 text-center">
          <div className="text-xs uppercase tracking-widest font-semibold text-secondary">{t("about.contact_eyebrow")}</div>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            {t("about.contact_title")}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            {t("about.contact_desc")}
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto text-sm">
            <div className="rounded-xl border border-border bg-card p-5">
              <MapPin size={18} weight="duotone" className="text-accent mx-auto mb-2" />
              <div className="font-semibold">{t("about.contact_location")}</div>
              <div className="text-xs text-muted-foreground mt-1">{t("about.contact_country")}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <Envelope size={18} weight="duotone" className="text-accent mx-auto mb-2" />
              <div className="font-semibold break-all">contact@nathortechnologies.com</div>
              <div className="text-xs text-muted-foreground mt-1">{t("about.contact_email_label")}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <Globe size={18} weight="duotone" className="text-accent mx-auto mb-2" />
              <div className="font-semibold">nathortechnologies.com</div>
              <div className="text-xs text-muted-foreground mt-1">{t("about.contact_site_label")}</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 text-xs text-muted-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>{t("about.footer_copyright")}</div>
          <div>
            <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground/80 hover:text-primary transition-colors" data-testid="link-nathor-footer">
              nathortechnologies.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
